import {
  FC,
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button } from 'antd';
import * as Icon from 'react-feather';
import styled from 'styled-components';

import { colorPrimary, controlHeight } from '@atlas-frontend/design-tokens';
import { useUserSelectBlockers } from '../UserSelectBlocker';

import { Stack } from './Stack';
import { useResizable } from '../useResizable';

const TriggerWrapper = styled(Stack)`
  width: 100%;
  height: 0;
  z-index: 3;
  position: relative;
`;

const TriggerButton = styled(Button)`
  position: relative;
  min-height: ${controlHeight}px;

  svg {
    vertical-align: middle !important;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const ChildrenWrapper = styled(ContentWrapper)`
  z-index: 1;
`;

export interface SidePanelProps extends PropsWithChildren {
  defaultCollapsed?: boolean;
  collapsedWidth?: number | string;
  width?: number;
  side?: 'left' | 'right';
  minWidth?: number;
  maxWidth?: number;
  mode?: 'normal' | 'layered' | 'modal';
  showToggleCollapseButton?: boolean;
  onResize?: (width: number) => void;
}

export const SidePanel: FC<SidePanelProps> = (props) => {
  const {
    defaultCollapsed = true,
    collapsedWidth = 20,
    minWidth = 200,
    width = 500,
    side = 'left',
    mode = 'normal',
    showToggleCollapseButton = true,
    onResize,
    children,
  } = props;
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const maxWidth = useMemo(
    () => clampWidth(props.maxWidth ?? 1786),
    [props.maxWidth]
  );

  const {
    targetRef,
    width: resizedWidth,
    isResizing,
    onMouseDown,
    onMouseUp,
    onMouseMove,
    setWidth,
  } = useResizable({
    initialWidth: clampWidth(defaultCollapsed ? collapsedWidth : width),
    minWidth,
    maxWidth,
    resizableSide: side === 'left' ? 'right' : 'left',
  });

  const openIcon = useMemo(
    () => (side === 'left' ? <Icon.ChevronRight /> : <Icon.ChevronLeft />),
    [side]
  );

  const closeIcon = useMemo(
    () => (side === 'left' ? <Icon.ChevronLeft /> : <Icon.ChevronRight />),
    [side]
  );

  const triggerIcon = useMemo(
    () => (isCollapsed ? openIcon : closeIcon),
    [openIcon, closeIcon, isCollapsed]
  );

  useEffect(() => {
    onResize?.(resizedWidth);
  }, [resizedWidth, onResize]);

  useEffect(() => {
    if (resizedWidth === minWidth) {
      setIsCollapsed(true);
    }
    if (resizedWidth === maxWidth) {
      setIsCollapsed(false);
    }
  }, [resizedWidth, minWidth, maxWidth]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(!isCollapsed);
    setWidth(Number(!isCollapsed ? collapsedWidth : clampWidth(width)));
  }, [closeIcon, collapsedWidth, isCollapsed, openIcon, setWidth, width]);

  const triggerButtonStyle = useMemo(
    () => ({
      ...(side === 'left' ? { left: 15 } : { right: 15 }),
      top: 16,
    }),
    [side]
  );

  const { add: addUserSelectBlocker } = useUserSelectBlockers();
  useEffect(() => {
    if (isResizing) {
      return addUserSelectBlocker();
    }
  }, [isResizing, addUserSelectBlocker]);

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const Wrapper =
    mode === 'modal'
      ? ModalWrapper
      : mode === 'layered'
      ? LayeredWrapper
      : Fragment;

  return (
    <Wrapper>
      <Container
        style={{
          width: resizedWidth,
          flexBasis: resizedWidth,
          transition: isResizing ? 'all 0s' : undefined,
          height: '100%',
        }}
      >
        {showToggleCollapseButton && (
          <TriggerWrapper size="sm" align={side === 'left' ? 'end' : 'start'}>
            <TriggerButton
              id={`toggle-${side}-side-panel`}
              type={'default'}
              shape={'circle'}
              icon={triggerIcon}
              onClick={toggleCollapse}
              style={triggerButtonStyle}
            />
          </TriggerWrapper>
        )}
        <ContentWrapper id="siderWrapper" ref={targetRef}>
          <ResizeTrigger $position={side} onMouseDown={onMouseDown} />
          <ChildrenWrapper>{children}</ChildrenWrapper>
        </ContentWrapper>
      </Container>
    </Wrapper>
  );
};

function clampWidth(width: number | string): number {
  const screenWidth = window.innerWidth;
  const maxAllowedWidth = screenWidth * 0.8;
  return Math.min(Number(width), Number(maxAllowedWidth));
}

type ResizeTriggerProps = {
  $position: 'left' | 'right';
};

const ResizeTrigger = styled.div<ResizeTriggerProps>`
  position: absolute;
  width: 8px;
  top: 0;
  bottom: 0;
  z-index: 2;
  cursor: ew-resize;

  ${(props) =>
    props.$position === 'left'
      ? `
    left: 100%;
    transform: translateX(-5px);`
      : `
    right: 100%;
    transform: translateX(5px);`}

  &:after {
    content: ' ';
    position: absolute;
    top: 0;
    left: 3px;
    background-color: transparent;
    transition: background-color 0.2s;
    width: 2px;
    height: 100%;
  }

  &:hover:after {
    background-color: ${colorPrimary};
  }
`;

const LayeredWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  z-index: 4;
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  z-index: 99;

  &:after {
    content: ' ';
    position: absolute;
    top: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.5);
    width: 100vw;
    height: 100vh;
    z-index: -1;
  }
`;

const Container = styled.div`
  position: relative;
  height: 100%;
  transition: all 0.2s, background 0s;
  background-color: white;
  flex: 1 1 auto;
`;
